import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TextField, Typography } from '@cfa/react-components';
import { Radio, RadioGroup } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import usePlanOperators from '@/hooks/usePlanOperators';
import usePlanOwner from '@/hooks/usePlanOwner';

import StepWizardPopUp, {
  type StepData,
  type StepWizardInstance,
} from '@/components/popups/StepWizardPopUp';

import { OperatorDto } from '@/_generated/api-clients/xp';

interface CorpLocation {
  name?: string;
  number?: string;
}

const getCorpLocations = (auth: unknown): CorpLocation[] => {
  if (typeof auth !== 'object' || auth === null) {
    return [];
  }

  const leaderCorpLocations = (auth as { leaderCorpLocations?: unknown })
    .leaderCorpLocations;

  if (!Array.isArray(leaderCorpLocations)) {
    return [];
  }

  return leaderCorpLocations
    .filter(
      (location): location is Record<string, unknown> =>
        typeof location === 'object' && location !== null,
    )
    .map(location => ({
      name: typeof location.name === 'string' ? location.name : undefined,
      number: typeof location.number === 'string' ? location.number : undefined,
    }));
};

interface CopyTemplatePopupProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  planName: string;
  submitHandler: ({
    name,
    assignedOperator,
    locationNumber,
  }: {
    name: string;
    assignedOperator?: OperatorDto;
    locationNumber?: string;
  }) => void;
  reopenTemplates: () => void;
}

const CopyTemplatePopup: React.FC<CopyTemplatePopupProps> = ({
  isOpen,
  setOpen,
  planName,
  reopenTemplates,
  submitHandler,
}) => {
  const { data: auth } = useAuth();
  const { operators } = usePlanOperators();
  const corpLocations = useMemo(() => getCorpLocations(auth), [auth]);
  const { shouldSelectOwner, isMultiOwner } = usePlanOwner();

  const ref = useRef<HTMLInputElement>(null);
  const initialStep = shouldSelectOwner ? 1 : 2;
  const [operator, setOperator] = useState<OperatorDto>();
  const [corpLocation, setCorpLocation] = useState<string>();
  const [operatorSelected, setOperatorSelected] = useState(false);
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [stepWizard, setStepWizard] = useState<StepWizardInstance | null>(null);
  const [input, setInput] = useState(
    `${planName} ${t('TrainingPlans.duplicatePlanInputCopy')}`,
  );

  const effectiveOperator =
    !isMultiOwner && !operator && operators.length === 1
      ? operators[0]
      : operator;
  const effectiveCorpLocation =
    !isMultiOwner && !corpLocation && corpLocations.length === 1
      ? corpLocations[0]?.number
      : corpLocation;

  const operatorsNameArray = [
    ...operators.map(o => o.name).filter(Boolean),
    ...corpLocations.map(c => c.name).filter(Boolean),
  ];

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setInput(`${planName} ${t('TrainingPlans.duplicatePlanInputCopy')}`);
    } else {
      if (shouldSelectOwner) {
        setOperator(undefined);
      }
      setStepIndex(initialStep);
      setOperatorSelected(false);
    }
  }

  useEffect(() => {
    if (!isOpen || stepIndex !== 2) return;
    const timer = window.setTimeout(() => {
      ref.current?.focus();
      ref.current?.select();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen, stepIndex]);

  const closePopup = () => {
    setStepIndex(initialStep);
    setOpen(false);
  };

  const selectOperator = (operatorName: string) => {
    const selectedOperator = operators.find(
      ({ name }) => name === operatorName,
    );
    const selectedCorpLocation = corpLocations.find(
      ({ name }) => name === operatorName,
    );
    setOperator(selectedOperator);
    setCorpLocation(selectedCorpLocation?.number);
    setOperatorSelected(true);
  };

  const Step1 = () => (
    <>
      <Typography className="mb-4!" variant="h3">
        {t('TrainingPlans.addPlanMenu.chooseOperator')}
      </Typography>
      <div className="flex justify-center">
        <RadioGroup
          defaultValue=""
          name="operatorList"
          orientation="vertical"
          onChange={selectOperator}
        >
          {operatorsNameArray
            .filter((operatorName): operatorName is string => !!operatorName)
            .map(operatorName => (
              <Radio key={operatorName} value={operatorName}>
                {operatorName}
              </Radio>
            ))}
        </RadioGroup>
      </div>
    </>
  );

  const Step2 = () => (
    <>
      <Typography variant="h3" className="mb-4!">
        {t('Generic.giveName', { object: t('Generic.plan') })}
      </Typography>
      <TextField
        className={'form-control'}
        name={'input'}
        onChange={e => setInput(e.target.value)}
        errorText={
          input.length
            ? undefined
            : `${t('Generic.planName')} ${t('TrainingPlans.requiredValue')}`
        }
        onKeyDown={e =>
          e.key === 'Enter' &&
          submitHandler({
            assignedOperator: effectiveOperator,
            locationNumber: effectiveCorpLocation,
            name: input,
          })
        }
        placeholder={t('TrainingPlans.planNamePlaceholder')}
        ref={ref}
        value={input}
      />
    </>
  );

  const stepsData: Record<number, StepData> = {
    1: {
      labelButtonDeny: t('Button.cancel'),
      labelButtonNext: t('Button.next'),
      onClickBack: () => {
        closePopup();
        reopenTemplates();
      },
      onClickNext: () => {
        stepWizard?.nextStep();
      },
      isDisabledButtonNext: !operatorSelected,
      ContentData: Step1,
    },
    2: {
      labelButtonDeny: shouldSelectOwner
        ? t('Button.back')
        : t('Button.cancel'),
      labelButtonNext: t('Button.create'),
      onClickBack: () => {
        if (shouldSelectOwner) {
          return stepWizard?.previousStep();
        }

        closePopup();
      },
      onClickNext: () => {
        submitHandler({
          assignedOperator: effectiveOperator,
          locationNumber: effectiveCorpLocation,
          name: input,
        });
      },
      isDisabledButtonNext: !input,
      ContentData: Step2,
    },
  };

  const step = stepsData[stepIndex] ?? stepsData[initialStep];

  if (!step) {
    throw new Error(`Invalid step index: ${stepIndex}`);
  }

  return (
    <StepWizardPopUp
      closePopup={closePopup}
      initialStep={initialStep}
      open={isOpen}
      setStepIndex={setStepIndex}
      setStepWizard={setStepWizard}
      step={step}
      steps={stepsData}
    />
  );
};

export default CopyTemplatePopup;
